import { buildCdnRenderPath } from "./buildCdnRenderPath";
import { buildCountryFlagPath } from "./buildCountryFlagPath";

/**
 * Country code utilities with FlagCDN integration and i18n support.
 * Client URLs use the first-party CDN; the server resolver uses FlagCDN as its source.
 * Uses Intl.DisplayNames for localized country names.
 */

/** ISO 3166-1 alpha-2 country codes recognised by the app (plus a few Discogs special codes). */
export const ALL_COUNTRY_CODES = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AL",
  "AM",
  "AO",
  "AR",
  "AT",
  "AU",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BN",
  "BO",
  "BR",
  "BS",
  "BT",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GH",
  "GM",
  "GN",
  "GQ",
  "GR",
  "GT",
  "GW",
  "GY",
  "HK",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IN",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MG",
  "MK",
  "ML",
  "MM",
  "MN",
  "MR",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NE",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PG",
  "PH",
  "PK",
  "PL",
  "PT",
  "PW",
  "PY",
  "QA",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SI",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SY",
  "SZ",
  "TD",
  "TG",
  "TH",
  "TJ",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VN",
  "VU",
  "WS",
  "XE",
  "XW",
  "YE",
  "ZA",
  "ZM",
  "ZW",
] as const;

export type CountryCode = (typeof ALL_COUNTRY_CODES)[number];

/**
 * Special codes that are not standard ISO codes or are historical.
 * These need manual translation as Intl.DisplayNames doesn't know them.
 */
const SPECIAL_CODES: Record<string, Record<string, string>> = {
  // Multi-region
  XE: { en: "Europe", pt: "Europa", es: "Europa" },
  XW: {
    en: "Worldwide",
    pt: "Mundial",
    es: "Mundial",
  },
  XU: {
    en: "Unknown",
    pt: "Desconhecido",
    es: "Desconocido",
  },
  XS: {
    en: "Scandinavia",
    pt: "Escandinávia",
    es: "Escandinavia",
  },
  XA: { en: "Asia", pt: "Ásia", es: "Asia" },
  XF: { en: "Africa", pt: "África", es: "África" },
  XC: {
    en: "Central America",
    pt: "América Central",
    es: "América Central",
  },
  XK: { en: "Kosovo", pt: "Kosovo", es: "Kosovo" },
  // Historical countries
  SU: { en: "USSR", pt: "URSS", es: "URSS" },
  YU: {
    en: "Yugoslavia",
    pt: "Iugoslávia",
    es: "Yugoslavia",
  },
  CS: {
    en: "Czechoslovakia",
    pt: "Tchecoslováquia",
    es: "Checoslovaquia",
  },
  DD: {
    en: "East Germany",
    pt: "Alemanha Oriental",
    es: "Alemania Oriental",
  },
  ZR: { en: "Zaire", pt: "Zaire", es: "Zaire" },
  BU: { en: "Burma", pt: "Birmânia", es: "Birmania" },
  RH: {
    en: "Rhodesia",
    pt: "Rodésia",
    es: "Rodesia",
  },
  VD: {
    en: "South Vietnam",
    pt: "Vietnã do Sul",
    es: "Vietnam del Sur",
  },
  AN: {
    en: "Netherlands Antilles",
    pt: "Antilhas Holandesas",
    es: "Antillas Holandesas",
  },
  DY: { en: "Dahomey", pt: "Daomé", es: "Dahomey" },
  HV: {
    en: "Upper Volta",
    pt: "Alto Volta",
    es: "Alto Volta",
  },
  EAZ: {
    en: "Zanzibar",
    pt: "Zanzibar",
    es: "Zanzíbar",
  },
};

const REGION_NAME_OVERRIDES: Record<string, Record<string, string>> = {
  // Node ICU and Chromium ICU disagree on this label in Portuguese, which causes SSR hydration drift.
  HK: { pt: "Hong Kong" },
};

/**
 * Get localized country name using Intl.DisplayNames.
 * Falls back to English if the locale is not supported.
 */
function getLocalizedCountryName(code: string, locale: string = "en"): string | undefined {
  const normalizedCode = code.toUpperCase();
  const lang = locale.split("-")[0] ?? locale;

  const specialCode = SPECIAL_CODES[code.toUpperCase()];
  if (specialCode) {
    return specialCode[locale] ?? specialCode[lang] ?? specialCode.en;
  }

  const override = REGION_NAME_OVERRIDES[normalizedCode];
  if (override) {
    return override[locale] ?? override[lang];
  }

  try {
    const displayNames = new Intl.DisplayNames([locale], { type: "region" });
    return displayNames.of(normalizedCode);
  } catch {
    try {
      const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
      return displayNames.of(normalizedCode);
    } catch {
      return undefined;
    }
  }
}

/** Map special/historical codes to FlagCDN codes. */
const FLAG_CODE_MAP: Record<string, string> = {
  XE: "eu",
  GB: "gb",
  SU: "ru",
  YU: "rs",
  CS: "cz",
  DD: "de",
  ZR: "cd",
  BU: "mm",
  RH: "zw",
  VD: "vn",
  AN: "nl",
  DY: "bj",
  HV: "bf",
  XK: "xk",
};

/** Codes that don't have flag images. */
const NO_FLAG_CODES = new Set(["XW", "XU", "XS", "XA", "XF", "XC", "EAZ"]);
const UNKNOWN_COUNTRY_CODE = "XU";

/** Returns null for codes without flags (e.g. XW/Worldwide). */
export function getFlagUrl(code: string, size: 20 | 40 | 80 | 160 | "svg" = 20): string | null {
  if (!code || NO_FLAG_CODES.has(code.toUpperCase())) {
    return null;
  }
  const path = buildCountryFlagPath(code);
  if (size === "svg") return path;
  return buildCdnRenderPath(path, { width: size, fit: "inside", format: "auto" });
}

/** Internal upstream source used by the country CDN route. */
export function getFlagSourceUrl(code: string): string | null {
  if (!code || NO_FLAG_CODES.has(code.toUpperCase())) return null;
  const flagCode = FLAG_CODE_MAP[code.toUpperCase()] || code.toLowerCase();
  return `https://flagcdn.com/${flagCode}.svg`;
}

/**
 * Get country name from code with i18n support.
 * Returns null if unknown.
 */
export function getCountryName(code: string, locale: string = "en"): string | null {
  if (!code) return null;
  return getLocalizedCountryName(code, locale) ?? null;
}
export interface FormatCountryOptions {
  includeUnknown?: boolean;
}

/**
 * Format country for display with i18n support.
 * Returns the country name if known, otherwise the code itself.
 */
export function formatCountry(
  code: string | null,
  locale: string = "en",
  options: FormatCountryOptions = {},
): string {
  if (!code) return "";
  const normalizedCode = code.toUpperCase();
  if (!options.includeUnknown && normalizedCode === UNKNOWN_COUNTRY_CODE) {
    return "";
  }
  return getLocalizedCountryName(normalizedCode, locale) ?? normalizedCode;
}

/**
 * Get unique countries from a list with their counts.
 * Useful for building country filter dropdowns.
 */
export function getCountryCounts(
  countries: (string | null)[],
  locale: string = "en",
  options: FormatCountryOptions = {},
): Array<{ code: string; name: string; count: number }> {
  const counts = new Map<string, number>();

  for (const code of countries) {
    if (code) {
      counts.set(code, (counts.get(code) ?? 0) + 1);
    }
  }

  return Array.from(counts.entries())
    .map(([code, count]) => ({
      code,
      name: formatCountry(code, locale, options),
      count,
    }))
    .sort((a, b) => b.count - a.count);
}
